// Shared sound layer for every surface that plays a term's pronunciation (Dictionary/rich view,
// Word Lists, Practice, Learn). One cache, one set of network/caching rules, so a fix here reaches
// everywhere instead of living in four near-identical copies.
//
// Two things a naive "just call api/sound/{termId} and play the url" approach gets wrong, which is
// why this exists:
//  1. Resolving the url is a DB+disk round trip on the server (SoundService) — worth caching by
//     itself, since the same word gets asked for repeatedly (a drill rung revisits it, a dictionary
//     row gets clicked more than once).
//  2. Even with the url cached, `new Audio(url).play()` still has to fetch the mp3 bytes over the
//     network the first time — and browser-level preloading of that fetch is NOT reliable enough to
//     build on: an <audio preload> element is subject to media-engagement/data-saver throttling and
//     can stall indefinitely on a backgrounded tab (seen in Chrome), and a plain fetch() doesn't
//     reliably warm what <audio> itself reads from (Firefox's media cache issues its own range
//     requests against a separate cache, so a fetch()-warmed entry can still be a miss there). So
//     prefetch owns the bytes directly — fetch → Blob → object URL — and play() reads from that
//     blob url when it's ready: memory, not a network layer, so behavior is identical everywhere.
window.PlSound = (function () {
	"use strict";

	// One entry per "term + heteronym variant" (a heteronym's rungs speak the sense being taught,
	// not the term default — same distinction the old per-surface code already made). Value shape:
	// { url: string|null, blobUrl: string|null }. null url = no clip THIS time; remembered so a
	// pre-load does not re-ask, but a press does (play() drops it first): since the per-user audio
	// caps (docs/project/tts-per-user-caps-plan.md R2/R4) "no clip" is often "not now" — a pre-load
	// past the soft cap, a clip over the pace — and the press is exactly what may get it.
	// Map preserves insertion order, which is what makes FIFO eviction trivial.
	var CACHE_CAP = 200;
	var EVICT_BATCH = 50;
	var cache = new Map();
	var pendingResolve = new Map();   // key -> Promise<url>, dedupes concurrent resolves of the same key
	var pendingBlob = new Map();      // key -> Promise<void>, dedupes concurrent blob fetches

	// dialect (optional, "US"/"UK") = which voice speaks — the rich card's per-dialect IPA chips
	// pass it. Part of the cache key: the US and UK clips of one term are DIFFERENT audio.
	//
	// ⚠️ An ordinary play passes NO dialect, and that does not mean "dialect-free" — it means "the
	// English I am learning", which the server resolves from the enDialect setting (SoundService).
	// So the key is tagged with the dialect currently in force even when the request will not carry
	// one. Two things follow, both of them the point:
	//   · flipping the chooser cannot serve a stale clip, because the old variant's entries are
	//     filed under the old tag and nothing looks them up any more;
	//   · flipping BACK is instant and free — the previous variant's urls and blobs are still there.
	// A learner who is still making up their mind pays one download per word per variant, ever.
	// A term in any other language is tagged too, which is harmless: nothing outside English has
	// variants, so at worst a German word is re-resolved once after a switch (one small request; the
	// clip itself is the same file, and the browser has it).
	function keyOf(termId, variantId, dialect) {
		return termId + ":" + (variantId || 0) + ":" + (dialect || currentDialect() || "");
	}

	// The dialect an un-named request should be understood to mean, or null when the question does
	// not arise (the learner is not learning English — nothing else has variants). Supplied by the
	// page rather than read from a global, because the answer depends on which language is being
	// learned and that changes without a reload; main.js owns that field, this module does not.
	var dialectProvider = null;
	function setDialectProvider(fn) { dialectProvider = fn; }
	function currentDialect() {
		return dialectProvider ? dialectProvider() : null;
	}

	// Bumped by dialectChanged(). The wire request for an ordinary play carries no dialect, so the
	// answer depends on WHEN it reaches the server: one issued a moment before the switch can be
	// answered a moment after it, in the new voice, and filing that under the old variant's key is
	// how a page ends up playing British under an American flag at random. A resolve or blob fetch
	// captures the epoch it started in and declines to file a result across a switch — it still
	// answers the caller that asked, since a play already under way wants a clip, not silence.
	var epoch = 0;

	// Evict the oldest EVICT_BATCH entries in one pass rather than trimming one at a time — cheap
	// bookkeeping on every insert instead of a loop, and avoids evicting more than needed since this
	// runs right after crossing the cap, not repeatedly while already over it.
	function evictIfNeeded() {
		if (cache.size <= CACHE_CAP) { return; }
		var n = 0;
		for (var key of cache.keys()) {
			if (n >= EVICT_BATCH) { break; }
			var entry = cache.get(key);
			if (entry && entry.blobUrl) { URL.revokeObjectURL(entry.blobUrl); }
			cache.delete(key);
			n++;
		}
	}

	// ⚠️ The dialect goes ON THE WIRE, even when the caller did not name one — that is what makes a
	// switch take effect on the very next press instead of on the next round trip. The alternative,
	// letting the server read the setting, means the answer depends on whether the chooser's save
	// POST has landed yet; a press (or the re-warm the switch itself kicks off) that overtakes it
	// gets the OLD voice back, and files it under the new variant's key, where nothing will ever
	// correct it. Naming the dialect makes the request self-contained: the same answer whenever it
	// arrives. The server still resolves an un-named one from the setting, for callers that have no
	// page to ask (and for anything that predates this).
	// prefetch = a preload is asking, not a press. The server serves the same clip either way and
	// only writes the flag on the spend-ledger row of an ElevenLabs generation the request causes —
	// so a TTS bill splits into words learners pressed and words a list warmed in case
	// (docs/project/sound-play-events.md). A play that joins an in-flight prefetch keeps its flag:
	// the preload is what caused that generation.
	function fetchSoundUrl(termId, variantId, dialect, prefetch) {
		var params = [];
		var wanted = dialect || currentDialect();
		if (variantId) { params.push("variant=" + variantId); }
		if (wanted) { params.push("dialect=" + encodeURIComponent(wanted)); }
		if (prefetch) { params.push("prefetch=1"); }
		return new Promise(function (resolve) {
			$.ajax({
				url: "api/sound/" + termId + (params.length ? "?" + params.join("&") : ""),
				type: "GET"
			})
				.done(function (sounds, status, xhr) {
					noteSignIn(xhr);
					resolve(sounds && sounds.length > 0 ? sounds[0].url : null);
				})
				.fail(function () { resolve(null); });
		});
	}

	// A guest's press refused on an audio limit an account lifts (docs/project/tts-per-user-caps-plan.md
	// R3/R5) — the sound GETs say so in a header and the body stays the clip list every caller reads.
	// The same invitation the dictionary gives a guest out of new words (ai_signIn_error), as a
	// message box because a press on a speaker has no slot to write a line into. At most once per
	// SIGN_IN_QUIET_MS: a guest pressing through a row of silent speakers is asked once, not per press.
	// Shared with main.js's phrase rows, which read the same header.
	var SIGN_IN_QUIET_MS = 2 * 60 * 1000;
	var signInAskedAt = 0;
	function noteSignIn(xhr) {
		if (!xhr || !xhr.getResponseHeader || !xhr.getResponseHeader("X-Audio-Sign-In")) { return; }
		var now = Date.now();
		if (now - signInAskedAt < SIGN_IN_QUIET_MS) { return; }
		signInAskedAt = now;
		if (window.showMessage) { showMessage(getRes("audio_signIn_message") || "Sign in to keep hearing new words."); }
	}

	// Resolves a cached url (calling cb synchronously) or fetches+caches it. cb always eventually
	// fires with the resolved url (or null — no sound available / lookup failed).
	function resolveKey(key, termId, variantId, dialect, cb, prefetch) {
		if (cache.has(key)) { cb(cache.get(key).url); return; }
		if (!pendingResolve.has(key)) {
			var at = epoch;
			var p = fetchSoundUrl(termId, variantId, dialect, prefetch).then(function (url) {
				// Still answer the caller that asked — it asked under the old setting and a play
				// already in flight finishing in the old voice is better than silence — but leave
				// the cache alone, and leave whatever pending entry the reset made room for.
				if (at !== epoch) { return url; }
				cache.set(key, { url: url, blobUrl: null });
				evictIfNeeded();
				pendingResolve.delete(key);
				pendingIsPreload.delete(key);
				return url;
			});
			pendingResolve.set(key, p);
			if (prefetch) { pendingIsPreload.add(key); }
		}
		pendingResolve.get(key).then(cb);
	}

	// Keys whose in-flight resolve was asked by a PRE-LOAD. A press that joins one and gets "no clip"
	// back asks again as a press (play()): the server may defer a pre-load (past the soft cap, over
	// the pace — tts-per-user-caps-plan.md R2/R4) and still generate the very same clip for a press.
	var pendingIsPreload = new Set();

	function preloadBlob(key, url) {
		if (!url || pendingBlob.has(key)) { return; }
		var entry = cache.get(key);
		if (entry && entry.blobUrl) { return; }   // already warmed
		var at = epoch;
		var p = fetch(url)
			.then(function (r) { return r.blob(); })
			.then(function (blob) {
				if (at !== epoch) { return; }   // warmed under a setting that no longer holds
				var e = cache.get(key);
				if (!e) { return; }   // evicted while the fetch was in flight — drop it, nothing to revoke yet
				e.blobUrl = URL.createObjectURL(blob);
			})
			.catch(function () { /* best-effort — a real play() falls back to fetching the url itself */ })
			.then(function () { if (at === epoch) { pendingBlob.delete(key); } });
		pendingBlob.set(key, p);
	}

	// Fire-and-forget: warms the url resolution AND the audio bytes for one word, well before
	// anyone actually tries to play it.
	function prefetch(termId, variantId, dialect) {
		if (termId == null) { return; }
		var key = keyOf(termId, variantId, dialect);
		resolveKey(key, termId, variantId, dialect, function (url) { preloadBlob(key, url); }, true);
	}

	// Batch prefetch for a known word list (Learn's session tray, Practice's card batch, a Dictionary/
	// Word-List viewport's visible rows). Dedupes within the batch itself (resolveKey/preloadBlob
	// already dedupe across overlapping calls, but skipping here avoids even the redundant lookups).
	// opts.cap bounds how much of a batch is actually prefetched (a Word List viewport passes 30 —
	// there is no reason to warm an entire 8000-word list at once); opts.staggerMs spreads the
	// resulting requests out instead of firing them all in the same tick, so many concurrent users
	// scrolling at once don't each produce a synchronized request burst.
	function prefetchMany(items, opts) {
		opts = opts || {};
		var cap = opts.cap || Infinity;
		var staggerMs = opts.staggerMs || 0;
		var seen = {};
		var deduped = [];
		for (var i = 0; i < items.length && deduped.length < cap; i++) {
			var it = items[i];
			if (!it || it.termId == null) { continue; }
			var key = keyOf(it.termId, it.variantId);
			if (seen[key]) { continue; }
			seen[key] = true;
			deduped.push(it);
		}
		deduped.forEach(function (it, idx) {
			if (staggerMs > 0) { setTimeout(function () { prefetch(it.termId, it.variantId); }, idx * staggerMs); }
			else { prefetch(it.termId, it.variantId); }
		});
	}

	// Sound is single-voice app-wide: every play() supersedes whatever's currently sounding,
	// anywhere (a Dictionary click while a Learn card is auto-playing stops the Learn one, etc).
	// playSeq guards against a resolve that lands after a newer play (or a stop()) superseded it.
	var curAudio = null;
	var playSeq = 0;

	function stop() {
		playSeq++;
		if (curAudio) {
			try { curAudio.pause(); curAudio.currentTime = 0; } catch (e) { /* ignore */ }
			curAudio = null;
		}
	}

	// variantId (optional) = heteronym pronunciation variant (0038): speaks the sense being shown,
	// not the term default. onEnded (optional) = Practice's sound-only rung wiring its own advance.
	// dialect (optional, "US"/"UK") = which dialect's voice — the per-dialect IPA chips pass it.
	function play(termId, variantId, onEnded, dialect) {
		if (termId == null) { return; }
		var seq = ++playSeq;
		var key = keyOf(termId, variantId, dialect);
		// A remembered "no clip" is a pre-load's answer; a press asks again (see the cache comment).
		var known = cache.get(key);
		if (known && !known.url) { cache.delete(key); }
		// …and a press joining a pre-load still in flight asks once more if the pre-load came back empty.
		var joinedPreload = pendingIsPreload.has(key);
		resolveKey(key, termId, variantId, dialect, function onUrl(url) {
			if (seq !== playSeq) { return; }   // superseded by a newer play or a stop() — drop it
			if (!url && joinedPreload) {
				joinedPreload = false;
				var e = cache.get(key);
				if (e && !e.url) { cache.delete(key); }
				resolveKey(key, termId, variantId, dialect, onUrl);
				return;
			}
			if (!url) { console.warn("sound: no audio available for term " + termId); return; }
			try {
				if (curAudio) { curAudio.pause(); }
				var entry = cache.get(key);
				// Prefer the blob a prefetch already downloaded — plays from memory, no network
				// involved. Falls back to the server url when nothing prefetched this word yet
				// (still triggers its own fetch, exactly like the pre-prefetch behavior).
				// T-13 (docs/project/tablet-support-plan.md §4): PL.soundElement is unset on a desktop.
				// js/touch/sound.js sets it — one element, unlocked by the first tap, for every play.
				curAudio = (window.PL && PL.soundElement) ? PL.soundElement((entry && entry.blobUrl) || url)
					: new Audio((entry && entry.blobUrl) || url);
				if (onEnded) { curAudio.onended = onEnded; }
				// Counted once it really STARTED — an autoplay refusal or a decode error is no play.
				curAudio.play().then(function () { notePlay("t", termId); }, function (e) {
					console.warn("sound: play failed for term " + termId, e && e.name);
				});
				preloadBlob(key, url);   // a play that beat its own prefetch still warms the cache for next time
			} catch (e) { /* autoplay policy */ }
		});
	}

	// The English dialect chooser moved (ai-features-plan.md §1 decision 6), which applies with no
	// reload. NOTHING IS THROWN AWAY: the tag in keyOf() has already moved every subsequent lookup
	// to the new variant's entries, so the old ones simply stop being asked for — and are waiting,
	// downloaded, if the reader switches back. All this does is close the in-flight window described
	// at `epoch`. Callers re-warm what is on screen themselves (main.js); the module deliberately
	// does not, because only the page knows what is currently in view.
	function dialectChanged() {
		epoch++;
		pendingIsPreload.clear();
	}

	// ── The sounds that actually played (docs/project/sound-play-events.md) ─────────────────────
	// A press on a preloaded word plays from the blob above and sends NOTHING, so the server cannot
	// count plays by itself — and the GET it does see is mostly preloads. So every play that started
	// is queued here and reported in one small POST: every ten minutes, and whenever the tab is
	// hidden (owner's cadence, 2026-10-05 — "after all it's just statistical info"). Hidden, not
	// "closed": a close event is not reliably delivered (a discarded tab, a sleeping laptop), while
	// visibilitychange → hidden fires on close, switch and minimise alike and is where sendBeacon is
	// meant to be called. Each entry carries its AGE at send time, not a clock reading: the server
	// files it at its own now minus that age, so a skewed client clock cannot misdate a play and a
	// late flush still lands on the right side of the 03:00 app-day cut. A crashed browser loses at
	// most one interval of plays — fine for statistics. A guest's plays are not queued: the server
	// keeps nothing without a user.
	// kind: "t" = a word (id = term id), "c" = a collocation, "e" = an example sentence.
	// ⛔ ABSOLUTE, unlike every other URL in this file: util.js roots a relative URL for $.ajax only,
	// and sendBeacon / fetch go round it — on /de/en/app a relative "api/…" is /de/en/api/… and 404s
	// (caught by temp/ui-smoke/sound-play-events-smoke.mjs, 2026-10-05).
	var PLAYED_URL = "/api/sound/played";
	var FLUSH_MS = 10 * 60 * 1000;
	var FLUSH_AT = 200;          // a long drill flushes early rather than growing one huge body
	var playQueue = [];

	function notePlay(kind, id) {
		if (id == null || window.guest_mode === true) { return; }
		playQueue.push({ k: kind, id: Number(id), t: Date.now() });
		if (playQueue.length >= FLUSH_AT) { flushPlays(); }
	}

	function flushPlays() {
		if (!playQueue.length) { return; }
		var now = Date.now();
		var body = JSON.stringify({ plays: playQueue.map(function (p) {
			return { k: p.k, id: p.id, age: Math.max(0, now - p.t) };
		}) });
		playQueue = [];
		try {
			if (navigator.sendBeacon
				&& navigator.sendBeacon(PLAYED_URL, new Blob([body], { type: "application/json" }))) {
				return;
			}
			fetch(PLAYED_URL, { method: "POST", body: body, keepalive: true,
				credentials: "same-origin", headers: { "Content-Type": "application/json" } })
				.catch(function () { /* best-effort statistics */ });
		} catch (e) { /* best-effort statistics */ }
	}

	setInterval(flushPlays, FLUSH_MS);
	document.addEventListener("visibilitychange", function () {
		if (document.visibilityState === "hidden") { flushPlays(); }
	});
	window.addEventListener("pagehide", flushPlays);

	return {
		play: play, stop: stop, prefetch: prefetch, prefetchMany: prefetchMany,
		dialectChanged: dialectChanged, setDialectProvider: setDialectProvider,
		notePlay: notePlay, noteSignIn: noteSignIn
	};
})();
